<template>
  <div class="onboarding">
    <div class="onboarding-title-wrapper">
      <h1 class="onboarding-title">{{ $t("custom.onboarding.title") }}</h1>
      <h2 class="onboarding-subtitle">{{ $t("custom.onboarding.subtitle") }}</h2>
    </div>

    <v-row
      class="onboarding-row"
      :class="[$vuetify.theme.dark ? 'onboarding-row--dark-bg' : 'onboarding-row--light-bg']">
      <div class="onboarding-img">
        <img :src="currentImage" />
      </div>
      <div class="onboarding-steps">
        <v-expansion-panels class="spacing-bottom--12 expansion-panel" ref="domain" :value="0">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-panel-title">
              <img class="expansion-panel-icon" :src="checkMarkImg" />
              {{ $t("custom.onboarding.domain.title") }}
            </v-expansion-panel-header>
            <v-expansion-panel-content class="expansion-panel-content">
              {{ $t("custom.onboarding.domain.content") }}
              <router-link class="expansion-panel-link" to="/domains">{{
                $t("custom.onboarding.domain.domains")
              }}</router-link>
              <span>.</span>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-expansion-panels class="spacing-bottom--12 expansion-panel" ref="header" :value="panels[0].open">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-panel-title">
              <div v-if="!panels[0].completed" class="expansion-panel-icon expansion-panel-number">2</div>
              <img v-else class="expansion-panel-icon" :src="checkMarkImg" />
              {{ $t("custom.onboarding.header.title") }}
            </v-expansion-panel-header>
            <v-expansion-panel-content class="expansion-panel-content">
              {{ $t("custom.onboarding.header.content") }}
              <br />
              <router-link class="expansion-panel-create" to="/headers">
                {{ $t("custom.onboarding.header.create") }} <v-icon>mdi-chevron-right</v-icon>
              </router-link>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-expansion-panels class="spacing-bottom--12 expansion-panel" ref="footer" :value="panels[1].open">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-panel-title">
              <div v-if="!panels[1].completed" class="expansion-panel-icon expansion-panel-number">3</div>
              <img v-else class="expansion-panel-icon" :src="checkMarkImg" />
              {{ $t("custom.onboarding.footer.title") }}</v-expansion-panel-header
            >
            <v-expansion-panel-content class="expansion-panel-content">
              {{ $t("custom.onboarding.footer.content") }}
              <br />
              <router-link to="/footers" class="expansion-panel-create">
                {{ $t("custom.onboarding.footer.create") }} <v-icon>mdi-chevron-right</v-icon>
              </router-link>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-expansion-panels class="spacing-bottom--12 expansion-panel" ref="page" :value="panels[2].open">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-panel-title">
              <div v-if="!panels[2].completed" class="expansion-panel-icon expansion-panel-number">4</div>
              <img v-else class="expansion-panel-icon" :src="checkMarkImg" />
              {{ $t("custom.onboarding.page.title") }}
            </v-expansion-panel-header>
            <v-expansion-panel-content class="expansion-panel-content">
              {{ $t("custom.onboarding.page.content") }}
              <br />
              <router-link to="/pages" class="expansion-panel-create">
                {{ $t("custom.onboarding.page.create") }} <v-icon>mdi-chevron-right</v-icon>
              </router-link>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="onboarding-hide" v-on:click="hideOnboarding(false)">
          {{ $t("custom.onboarding.hide") }}
        </div>
      </div>
    </v-row>
  </div>
</template>

<script>
  import headerLightImg from "@/assets/images/svg/onboarding-header-light.svg";
  import headerDarkImg from "@/assets/images/svg/onboarding-header-dark.svg";
  import footerLightImg from "@/assets/images/svg/onboarding-footer-light.svg";
  import footerDarkImg from "@/assets/images/svg/onboarding-footer-dark.svg";
  import pageLightImg from "@/assets/images/svg/onboarding-page-light.svg";
  import pageDarkImg from "@/assets/images/svg/onboarding-page-dark.svg";
  import domainLightImg from "@/assets/images/svg/onboarding-domain-light.svg";
  import domainDarkImg from "@/assets/images/svg/onboarding-domain-dark.svg";
  import checkmark from "@/assets/images/svg/checkmark.svg";
  import checkmarkDark from "@/assets/images/svg/checkmark-dark.svg";
  import { eventBus } from "@/event-bus";
  import { mixpanelWrapper } from "@/@core/libs/mixpanel";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    data() {
      return {
        imagesBlockVisible: false,
        currentImage: "",
        panels: [{ completed: false }, { completed: false }, { completed: false }],
        domainFn: () => this.changePicture(this.$vuetify.theme.dark ? domainDarkImg : domainLightImg),
        headerFn: () => this.changePicture(this.$vuetify.theme.dark ? headerDarkImg : headerLightImg),
        footerFn: () => this.changePicture(this.$vuetify.theme.dark ? footerDarkImg : footerLightImg),
        pageFn: () => this.changePicture(this.$vuetify.theme.dark ? pageDarkImg : pageLightImg),
      };
    },
    computed: {
      checkMarkImg() {
        return this.$vuetify.theme.dark ? checkmarkDark : checkmark;
      },
    },
    async mounted() {
      this.$refs.domain.$el.addEventListener("mouseover", this.domainFn);
      this.$refs.header.$el.addEventListener("mouseover", this.headerFn);
      this.$refs.footer.$el.addEventListener("mouseover", this.footerFn);
      this.$refs.page.$el.addEventListener("mouseover", this.pageFn);

      try {
        const { data } = await this.$admin.dataProvider.customHttpCall(
          "GET",
          `organizations/${this.$store.getters["getActiveOrganization"].organization.id}/resources`,
        );
        this.panels = [
          {
            completed: data.headers > 0,
            open: false,
          },
          {
            completed: data.footers > 0,
            open: false,
          },
          {
            completed: data.pages > 0,
            open: false,
          },
        ];
        //Open first incomplete step
        for (let i = 0; i < this.panels.length; i++) {
          if (!this.panels[i].completed) {
            this.panels[i].open = 0;
            break;
          }
          if (i === this.panels.length - 1) {
            this.hideOnboarding(true);
          }
        }
      } catch (e) {
        this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
      }
    },
    beforeDestroy() {
      this.$refs.domain.$el.removeEventListener("mouseover", this.pageFn);
      this.$refs.header.$el.removeEventListener("mouseover", this.headerFn);
      this.$refs.footer.$el.removeEventListener("mouseover", this.footerFn);
      this.$refs.page.$el.removeEventListener("mouseover", this.pageFn);
    },
    watch: {
      "$vuetify.theme.dark": {
        handler(newVal) {
          this.currentImage = newVal ? domainDarkImg : domainLightImg;
        },
        immediate: true,
      },
    },
    methods: {
      changePicture(picture) {
        this.currentImage = picture;
      },
      hideOnboarding(completed) {
        if (completed) {
          // Auto hiding onboarding only the first time
          if (!this.$store.getters["getCurrentUser"].onboardingComplete) {
            this.updateOnboarding();
            mixpanelWrapper.track("Onboarding Completed", { previous_page: this.$store.getters["getPreviousRoute"] });
          }
          this.$store.commit("SET_USER_ONBOARDING_COMPLETE", true);
        } else {
          this.updateOnboarding();
          this.$admin.toast.success(this.$t("custom.onboarding.hidden_toast"));
          mixpanelWrapper.track("Onboarding Closed", { previous_page: this.$store.getters["getPreviousRoute"] });
          eventBus.$emit("open-menu", true);
        }
      },
      updateOnboarding() {
        this.$store.commit("SET_USER_ONBOARDED", true);
        this.$admin.dataProvider.customHttpCall("PATCH", `users/${this.$store.getters["getCurrentUser"].id}`, {
          onboarded: true,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../@core/preset/preset/typography/index.scss";
  @import "../@core/preset/preset/functions";
  .onboarding {
    position: relative;
    max-width: 1000px;
    margin-right: auto;
    margin-left: auto;
    &-title {
      margin-top: 12px;
      @include heading-2();
      color: get-color("headline");
    }
    &-subtitle {
      @include text-subtitle-1();
      color: get-color("subtitle");
    }
    &-title-wrapper {
      margin-bottom: 64px;
      text-align: center;
    }
    &-row {
      &::before {
        content: "";
        border-radius: 10px;
        z-index: 0;
        left: 12%;
        width: 93%;
        position: absolute;
        height: 100%;
        max-height: 500px;
        top: 169px;
      }
      &--light-bg {
        &::before {
          background: linear-gradient(306deg, rgba(222, 219, 255, 0.21) 0%, rgba(222, 219, 255, 0.12) 100%);
        }
      }
      &--dark-bg {
        &::before {
          background: linear-gradient(
            307deg,
            rgba(255, 255, 255, 0.02) 0%,
            rgba(179, 104, 254, 0.02) 0.01%,
            rgba(141, 122, 255, 0.06) 100%
          );
        }
      }
    }

    &-img {
      z-index: 1;
      flex: 2;
      margin-right: 50px;
      display: flex;
      align-items: center;
      & img {
        width: 100%;
        height: auto;
        max-height: 500px;
      }
    }

    &-steps {
      flex: 1.3;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &-hide {
      color: get-color("secondary-btn");
      @include text-button-small();
      text-align: right;
      margin-top: 12px;
      position: relative;
      z-index: 2;

      &:hover {
        cursor: pointer;
        color: get-color("primary-hover");
      }
    }
  }

  .expansion-panel {
    border-radius: 10px;
    box-shadow: 0px 2px 10px 0px rgba(69, 64, 78, 0.1);
    border-bottom: 2px solid get-color("white");
    &:hover {
      border-color: get-color("primary");
    }
    & .v-expansion-panel {
      &.v-expansion-panel--active {
        border-radius: 8px !important;
        box-shadow: none !important;
      }
      &::before {
        box-shadow: none !important;
      }
    }

    &-title {
      @include text-body-2-bold();
      padding: 16px 16px 16px 24px !important;
      min-height: unset !important;
    }
    &-content {
      @include text-caption();
      ::v-deep {
        & > div {
          padding: 0px 16px 24px 24px !important;
        }
      }
    }
    &-icon {
      flex: 0 !important;
      position: absolute;
      left: -12px;
    }
    &-number {
      height: 24px;
      width: 24px;
      background-color: get-color("primary-20");
      color: get-color("primary");
      border-radius: 40px;
      padding: 8px;
      font-size: 12px;
      font-weight: 700;
      line-height: 7px;
    }
    &-create {
      display: flex;
      align-items: center;
      margin-top: 16px;
      text-decoration: none;
      color: get-color("secondary-btn");
      &:hover {
        color: get-color("primary-hover");
        & i {
          color: get-color("primary-hover");
        }
      }

      & i {
        color: get-color("secondary-btn");
        transition: none;
        width: 16px;
        height: 16px;
        margin-left: 4px;
      }
    }

    &-link {
      color: get-color("secondary-btn");
      text-decoration: none;

      &:hover {
        color: get-color("primary-hover");
      }
    }
  }
</style>
